import useAdminToast from '../useAdminToast';
import AdminState from '../AdminState';
import { AdminAlertIcon as AlertIcon, RepeatIcon } from '../AdminIcon';
// Components/Tabs/EmailDebugTab.jsx

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Slider from "../Slider/Slider";
import Backend from "../../../utils/api.js";
import {
  Box,
  Text,
  VStack,
  HStack,
  Badge,
  Button,
  Alert,
  IconButton,
  Code,
} from '@chakra-ui/react';

import "./EmailDebugTab.css";

/**
 * EmailDebugTab
 *
 * Adminland "Debugging" section for diagnosing swipe-out reminder email
 * issues (DBG-F1/F2/F3) without needing backend log access: an "Email
 * Health" card surfaces SparkPost configuration and the nightly cron's
 * schedule, and a "Test email" card lets a super admin send a real test
 * email on demand and see the SparkPost result immediately.
 */
function EmailDebugTab({ user, pushTab, revert }) {
  const toast = useAdminToast();
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusError, setStatusError] = useState(null);

  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    setStatusLoading(true);
    setStatusError(null);
    try {
      const response = await Backend.adminGetEmailStatus(user.UID);
      if (response.status === 200 || response.status === 202) {
        setStatus(response);
      } else if (response.status === 401 || response.status === 403) {
        setStatusError("You do not have permission to view email status");
      } else {
        setStatusError("Failed to load email status");
      }
    } catch (err) {
      console.error("Error loading email status:", err);
      setStatusError("Failed to connect to the server");
    } finally {
      setStatusLoading(false);
    }
  };

  const handleSendTest = async () => {
    setSending(true);
    setSendResult(null);
    try {
      // Always sends to the calling admin's own registered email -- the
      // backend doesn't accept a client-supplied recipient (rate-limited
      // to once every 15 seconds per admin either way).
      const response = await Backend.adminSendTestEmail(user.UID);
      if (response.status === 200 || response.status === 202) {
        toast({ title: response.status === 202 ? "Test email queued" : "Test email sent", description: response.message });
      } else if (response.status === 429) {
        const retryAfter = response.retry_after;
        setSendResult({
          success: false,
          message: retryAfter
            ? `Please wait ${retryAfter}s before sending another test email`
            : "Please wait before sending another test email",
          detail: null,
        });
      } else {
        setSendResult({ success: false, message: response.message || "Failed to send test email", detail: response.error });
      }
    } catch (err) {
      console.error("Error sending test email:", err);
      setSendResult({ success: false, message: "Failed to connect to the server", detail: null });
    } finally {
      setSending(false);
      loadStatus();
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Slider
      name="Email debugging"
      navColor="rgb(220, 53, 69)"
      slide={true}
      index={0}
      onClick={() => revert(1)}
    >
      <div className="EmailDebugTab">
        <VStack align="stretch" spacing={4}>
          {/* Email health card */}
          <Box className="email-debug-card">
            <HStack justify="space-between" mb={3} spacing={0} gap={3} flexWrap="wrap">
              <Text as="h3" fontSize="xl" fontWeight="semibold">Email health</Text>
              <IconButton
                icon={<RepeatIcon />}
                onClick={loadStatus}
                aria-label="Refresh email health"
                isLoading={statusLoading}
                size="sm"
                variant="outline"
              />
            </HStack>

            {statusLoading && !status ? (
              <AdminState status="loading" title="Loading email health…" mb={0} />
            ) : statusError ? (
              <AdminState status="error" title="Email health could not be loaded" description={statusError} onRetry={loadStatus} mb={0} />
            ) : status ? (
              <VStack align="stretch" spacing={3} fontSize="sm">
                <HStack className="admin-detail-row">
                  <Text fontWeight="semibold" minW="180px">SparkPost API key:</Text>
                  <Badge colorScheme={status.sparkpost_key_configured ? "green" : "red"}>
                    {status.sparkpost_key_configured ? "Configured" : "Not configured"}
                  </Badge>
                </HStack>
                <HStack className="admin-detail-row">
                  <Text fontWeight="semibold" minW="180px">Email templates:</Text>
                  <Badge colorScheme={status.templates_configured ? "green" : "red"}>
                    {status.templates_configured ? "Configured" : "Not configured"}
                  </Badge>
                </HStack>
                <HStack className="admin-detail-row">
                  <Text fontWeight="semibold" minW="180px">Nightly job scheduled:</Text>
                  {status.scheduled_job?.next_run_time ? (
                    <>
                      <Badge colorScheme="green">Scheduled</Badge>
                      <Text color="gray.600">
                        next run {formatDateTime(status.scheduled_job.next_run_time)}
                      </Text>
                    </>
                  ) : (
                    <Badge colorScheme="yellow">Not scheduled</Badge>
                  )}
                </HStack>
                <Box pt={2} borderTop="1px solid #e2e8f0">
                  <Text fontWeight="semibold" mb={1}>Last test send:</Text>
                  {status.last_test_send ? (
                    <VStack align="start" spacing={1} pl={2}>
                      <HStack className="admin-detail-row">
                        <Badge colorScheme={["pending", "sending", "unknown"].includes(status.last_test_send.state) ? "yellow" : status.last_test_send.success ? "green" : "red"}>
                          {({ pending: "Queued", sending: "Sending", unknown: "Delivery unconfirmed", sent: "Sent" })[status.last_test_send.state] || (status.last_test_send.success ? "Success" : "Failed")}
                        </Badge>
                        <Text color="gray.600">
                          to {status.last_test_send.recipient} at {formatDateTime(status.last_test_send.timestamp)}
                        </Text>
                      </HStack>
                      {status.last_test_send.error && (
                        <Text color="red.600" fontSize="xs">{status.last_test_send.error}</Text>
                      )}
                    </VStack>
                  ) : (
                    <AdminState title="No test email sent yet" description="Send a test email below to check delivery to your registered email address." mb={0} />
                  )}
                </Box>
              </VStack>
            ) : null}
          </Box>

          {/* Test email card */}
          <Box className="email-debug-card">
            <Text as="h3" fontSize="xl" fontWeight="semibold" mb={3}>Test email</Text>
            <Text fontSize="sm" color="gray.600" mb={3}>
              Sends a real checkout reminder email via SparkPost to your own registered
              admin email address, so you can confirm delivery is working without waiting
              for the nightly job. Limited to one send every 15 seconds.
            </Text>
            <Button
              colorScheme="blue"
              onClick={handleSendTest}
              isLoading={sending}
            >
              Send test email to myself
            </Button>

            {sendResult && (
              <Alert status={sendResult.success ? "success" : "error"} mt={3} borderRadius="md" flexDirection="column" alignItems="start">
                <HStack className="admin-detail-row">
                  <AlertIcon status={sendResult.success ? "success" : "error"} />
                  <Text>{sendResult.message}</Text>
                </HStack>
                {sendResult.detail && (
                  <Code
                    display="block"
                    whiteSpace="pre-wrap"
                    overflowWrap="anywhere"
                    p={2}
                    mt={2}
                    borderRadius="md"
                    fontSize="xs"
                    maxH="150px"
                    overflowY="auto"
                    w="100%"
                  >
                    {typeof sendResult.detail === "string" ? sendResult.detail : JSON.stringify(sendResult.detail, null, 2)}
                  </Code>
                )}
              </Alert>
            )}
          </Box>
        </VStack>
      </div>
    </Slider>
  );
}

EmailDebugTab.propTypes = {
  user: PropTypes.object.isRequired,
  pushTab: PropTypes.func.isRequired,
  revert: PropTypes.func.isRequired,
};

export default EmailDebugTab;
